import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Badge, Button, Input, Modal, Toggle } from '@/components/ui';
import {
  updateCaseSettings,
  type HrCaseOption,
  type HrCaseSettings,
  type HrCaseSettingsInput,
  type HrWarningLevel,
} from '@/api/services/hrCases.service';
import { getErrorMessage } from '@/lib/getErrorMessage';
import { cn } from '@/lib/cn';
import { keyFromLabel, LABEL_CLASS } from './caseFormat';

type NumberField =
  | 'hearingNoticeDays'
  | 'appealWindowDays'
  | 'activeWarningsBeforeNextLevel'
  | 'acknowledgeWithinDays';

const DISCIPLINE_NUMBERS: Array<{
  key: NumberField;
  label: string;
  min: number;
  max: number;
  hint: string;
}> = [
  {
    key: 'hearingNoticeDays',
    label: 'Hearing notice (days)',
    min: 0,
    max: 60,
    hint: 'Notice the person gets before a hearing. Less needs a recorded reason.',
  },
  {
    key: 'appealWindowDays',
    label: 'Appeal window (days)',
    min: 1,
    max: 90,
    hint: 'Time to appeal, counted from the decision.',
  },
  {
    key: 'activeWarningsBeforeNextLevel',
    label: 'Warnings per level',
    min: 1,
    max: 10,
    hint: 'Active warnings before the next level is suggested. Nothing escalates on its own.',
  },
];

const GRIEVANCE_NUMBERS = [
  {
    key: 'acknowledgeWithinDays' as const,
    label: 'Acknowledge within (days)',
    min: 1,
    max: 60,
    hint: 'Handlers are reminded, then the alternates are told.',
  },
];

function OptionList({
  legend,
  noun,
  example,
  options,
  onChange,
  disabled,
}: {
  legend: string;
  /** Singular, lower case: "case type". */
  noun: string;
  example: string;
  options: HrCaseOption[];
  onChange: (next: HrCaseOption[]) => void;
  disabled: boolean;
}): React.ReactElement {
  const [draft, setDraft] = useState('');
  const newKey = keyFromLabel(draft);
  const duplicate = options.some((option) => option.key === newKey);
  const canAdd = draft.trim() !== '' && !duplicate;
  const add = (): void => {
    if (!canAdd) return;
    onChange([...options, { key: newKey, label: draft.trim(), active: true }]);
    setDraft('');
  };
  return (
    <fieldset disabled={disabled} className="space-y-2">
      <legend className={LABEL_CLASS}>{legend}</legend>
      <ul className="divide-y divide-gray-200 rounded-xl border border-gray-200 dark:divide-gray-700 dark:border-gray-700">
        {options.map((option, index) => (
          <li key={option.key} className="flex items-center gap-3 py-2 pl-2 pr-3">
            <Input
              aria-label={`${legend}: name of ${option.label || option.key}`}
              className={cn(
                'h-9 border-transparent bg-transparent shadow-none hover:border-gray-200 dark:bg-transparent dark:hover:border-gray-600',
                !option.active &&
                  'text-gray-500 line-through decoration-gray-300 dark:text-gray-400'
              )}
              value={option.label}
              onChange={(event) =>
                onChange(
                  options.map((item, i) =>
                    i === index ? { ...item, label: event.target.value } : item
                  )
                )
              }
            />
            <Toggle
              id={`${keyFromLabel(legend)}-${option.key}-active`}
              checked={option.active}
              onChange={(active) =>
                onChange(options.map((item, i) => (i === index ? { ...item, active } : item)))
              }
              aria-label={`Offer ${option.label || option.key} on new cases`}
              size="sm"
            />
            <span aria-hidden className="w-12 shrink-0 text-xs text-gray-600 dark:text-gray-300">
              {option.active ? 'In use' : 'Off'}
            </span>
          </li>
        ))}
      </ul>
      <p className="text-xs text-gray-600 dark:text-gray-300">
        Turning one off stops it being offered on new cases. Cases that already use it keep it.
      </p>
      <div className="flex items-start gap-2 pt-1">
        <div className="min-w-0 flex-1">
          <Input
            id={`case-settings-add-${keyFromLabel(legend)}`}
            aria-label={`New ${noun}`}
            placeholder={`Add a ${noun}, for example ${example}`}
            value={draft}
            error={draft && duplicate ? `That ${noun} already exists.` : undefined}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              // Enter adds the option instead of submitting the whole form.
              if (event.key === 'Enter') {
                event.preventDefault();
                add();
              }
            }}
          />
        </div>
        <Button
          type="button"
          variant="secondary"
          className="h-11 shrink-0"
          disabled={!canAdd}
          onClick={add}
        >
          <Plus className="mr-1 h-4 w-4" aria-hidden />
          Add
        </Button>
      </div>
    </fieldset>
  );
}

/**
 * Company discipline and grievance settings. Mounted only while open, so it
 * starts from the saved values. Options are deactivated rather than deleted
 * because open cases refer to them by key.
 */
export function CaseSettingsDialog({
  companyId,
  settings,
  canEditDiscipline,
  canEditGrievances,
  onClose,
}: {
  companyId: string;
  settings: HrCaseSettings;
  canEditDiscipline: boolean;
  canEditGrievances: boolean;
  onClose: () => void;
}): React.ReactElement {
  const queryClient = useQueryClient();
  const [types, setTypes] = useState(settings.disciplineCaseTypes);
  const [categories, setCategories] = useState(settings.grievanceCategories);
  const [levels, setLevels] = useState<HrWarningLevel[]>(settings.warningLevels);
  const [numbers, setNumbers] = useState<Record<NumberField, string>>(() => ({
    hearingNoticeDays: String(settings.hearingNoticeDays),
    appealWindowDays: String(settings.appealWindowDays),
    activeWarningsBeforeNextLevel: String(settings.activeWarningsBeforeNextLevel),
    acknowledgeWithinDays: String(settings.acknowledgeWithinDays),
  }));
  const [formError, setFormError] = useState<string | null>(null);

  const numberErrors: Partial<Record<NumberField, string>> = {};
  for (const { key, label, min, max } of [...DISCIPLINE_NUMBERS, ...GRIEVANCE_NUMBERS]) {
    const raw = numbers[key].trim();
    if (!/^\d+$/.test(raw) || Number(raw) < min || Number(raw) > max) {
      numberErrors[key] = `${label.replace(/ \(.*\)$/, '')}: whole number from ${min} to ${max}.`;
    }
  }
  const levelErrors = levels.map((level) =>
    !level.label.trim()
      ? 'Give this level a name.'
      : !Number.isInteger(level.expiryDays) || level.expiryDays < 1 || level.expiryDays > 3650
        ? 'Expiry must be 1 to 3650 days.'
        : null
  );
  const optionError =
    !types.some((item) => item.active) || !categories.some((item) => item.active)
      ? 'Keep at least one active case type and grievance category.'
      : types.concat(categories).some((item) => !item.label.trim())
        ? 'Every case type and category needs a name.'
        : null;
  const valid =
    Object.keys(numberErrors).length === 0 &&
    levelErrors.every((error) => error === null) &&
    optionError === null;

  const save = useMutation({
    mutationFn: (input: HrCaseSettingsInput) => updateCaseSettings(companyId, input),
    onSuccess: () => {
      toast.success('Case settings saved');
      void queryClient.invalidateQueries({ queryKey: ['hr-case-settings', companyId] });
      onClose();
    },
    onError: (mutationError) => {
      const message = getErrorMessage(mutationError);
      setFormError(message);
      toast.error(message);
    },
  });

  const handleSubmit = (): void => {
    if (!valid) return;
    setFormError(null);
    // Each capability may change only its own half; send only that half.
    save.mutate({
      ...(canEditDiscipline && {
        disciplineCaseTypes: types,
        warningLevels: levels,
        hearingNoticeDays: Number(numbers.hearingNoticeDays),
        appealWindowDays: Number(numbers.appealWindowDays),
        activeWarningsBeforeNextLevel: Number(numbers.activeWarningsBeforeNextLevel),
      }),
      ...(canEditGrievances && {
        grievanceCategories: categories,
        acknowledgeWithinDays: Number(numbers.acknowledgeWithinDays),
      }),
    });
  };

  const numberInput = ({
    key,
    label,
    min,
    max,
    hint,
  }: (typeof DISCIPLINE_NUMBERS)[number]): React.ReactElement => (
    <Input
      key={key}
      id={`case-settings-${key}`}
      label={label}
      type="number"
      inputMode="numeric"
      min={min}
      max={max}
      step={1}
      value={numbers[key]}
      error={numberErrors[key]}
      hint={numberErrors[key] ? undefined : hint}
      onChange={(event) => setNumbers((current) => ({ ...current, [key]: event.target.value }))}
    />
  );

  const readOnlyNote = (permission: string): React.ReactElement => (
    <Badge variant="neutral" size="sm" className="ml-2 align-middle font-normal">
      Read only: needs the {permission} permission
    </Badge>
  );

  return (
    <Modal
      open
      onClose={onClose}
      title="Case settings"
      className="max-w-2xl"
      footer={
        <>
          {(optionError || formError) && (
            <p className="mr-auto text-sm text-danger" role="alert">
              {formError ?? optionError}
            </p>
          )}
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="case-settings-form"
            disabled={!valid || save.isPending || (!canEditDiscipline && !canEditGrievances)}
            loading={save.isPending}
          >
            Save settings
          </Button>
        </>
      }
    >
      <form
        id="case-settings-form"
        className="space-y-8"
        onSubmit={(event) => {
          event.preventDefault();
          handleSubmit();
        }}
      >
        <div className="space-y-2">
          <Badge variant={settings.isDefault ? 'neutral' : 'info'} size="sm">
            {settings.isDefault ? 'Standard settings' : 'Custom settings'}
          </Badge>
          <p className="text-sm text-gray-600 dark:text-gray-300">
            New cases use these settings from the day they open. Cases already open keep the
            settings they started with.
          </p>
        </div>

        <section className="space-y-5" aria-labelledby="case-settings-discipline">
          <div>
            <h3
              id="case-settings-discipline"
              className="text-base font-semibold text-gray-900 dark:text-gray-100"
            >
              Discipline
              {!canEditDiscipline && readOnlyNote('discipline')}
            </h3>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
              Used when HR opens a case about someone’s conduct or performance.
            </p>
          </div>
          <OptionList
            legend="Case types"
            noun="case type"
            example="Health and safety"
            options={types}
            onChange={setTypes}
            disabled={!canEditDiscipline}
          />
          <fieldset disabled={!canEditDiscipline} className="space-y-2">
            <legend className={LABEL_CLASS}>Warning levels, least to most serious</legend>
            <div
              aria-hidden
              className="grid grid-cols-[1.5rem_minmax(0,1fr)_8.5rem] gap-2 text-xs text-gray-600 dark:text-gray-300"
            >
              <span />
              <span>Name</span>
              <span>Expires after</span>
            </div>
            <ol className="space-y-2">
              {levels.map((level, index) => (
                <li key={level.key}>
                  <div className="grid grid-cols-[1.5rem_minmax(0,1fr)_8.5rem] items-center gap-2">
                    <span
                      aria-hidden
                      className="flex h-6 w-6 items-center justify-center rounded-full bg-gray-100 text-xs font-medium tabular-nums text-gray-700 dark:bg-gray-700 dark:text-gray-200"
                    >
                      {index + 1}
                    </span>
                    <Input
                      aria-label={`Warning level ${index + 1} name`}
                      value={level.label}
                      aria-invalid={!level.label.trim() || undefined}
                      onChange={(event) =>
                        setLevels(
                          levels.map((item, i) =>
                            i === index ? { ...item, label: event.target.value } : item
                          )
                        )
                      }
                    />
                    <div className="relative">
                      <Input
                        aria-label={`Warning level ${index + 1} expires after, in days`}
                        type="number"
                        inputMode="numeric"
                        min={1}
                        max={3650}
                        className="pr-12 tabular-nums"
                        value={String(level.expiryDays)}
                        onChange={(event) =>
                          setLevels(
                            levels.map((item, i) =>
                              i === index
                                ? { ...item, expiryDays: Number(event.target.value) }
                                : item
                            )
                          )
                        }
                      />
                      <span
                        aria-hidden
                        className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-sm text-gray-500 dark:text-gray-400"
                      >
                        days
                      </span>
                    </div>
                  </div>
                  {levelErrors[index] && (
                    <p className="ml-8 mt-1 text-xs text-danger" role="alert">
                      {levelErrors[index]}
                    </p>
                  )}
                </li>
              ))}
            </ol>
            <p className="text-xs text-gray-600 dark:text-gray-300">
              A warning stops counting at the end of the company’s local day, this many days after
              it is issued.
            </p>
          </fieldset>
          <fieldset disabled={!canEditDiscipline} className="grid gap-4 sm:grid-cols-3">
            <legend className="sr-only">Time limits</legend>
            {DISCIPLINE_NUMBERS.map(numberInput)}
          </fieldset>
        </section>

        <section
          className="space-y-5 border-t border-gray-100 pt-6 dark:border-gray-700"
          aria-labelledby="case-settings-grievances"
        >
          <div>
            <h3
              id="case-settings-grievances"
              className="text-base font-semibold text-gray-900 dark:text-gray-100"
            >
              Grievances
              {!canEditGrievances && readOnlyNote('grievances')}
            </h3>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
              Used when an employee raises a complaint for HR to look into.
            </p>
          </div>
          <OptionList
            legend="Categories"
            noun="category"
            example="Workload"
            options={categories}
            onChange={setCategories}
            disabled={!canEditGrievances}
          />
          <fieldset disabled={!canEditGrievances} className="grid gap-4 sm:grid-cols-3">
            <legend className="sr-only">Time limits</legend>
            {GRIEVANCE_NUMBERS.map(numberInput)}
          </fieldset>
        </section>
      </form>
    </Modal>
  );
}
